import type { CreatePageStudy, PageStudy, StudyPage } from "@crux/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addPageHitsToBrand,
  fetchPageHits,
  fetchPageStudies,
  fetchPageStudy,
  startPageStudy,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";

/** Prompts for a page. */

/** While a page is still being written or asked, look again every few seconds. */
const SEARCH_POLL_MS = 4_000;

/**
 * True while a page is still being prepared, has captures out, or — searched
 * in rounds — has another round to come: between rounds every capture is back
 * and the page is still not done.
 */
export function isSearchingPage(page: StudyPage): boolean {
  return (
    page.writeState === "waiting" ||
    page.answered < page.asked ||
    page.loopState === "searching" ||
    page.loopState === "confirming"
  );
}

/** True while any page is still searching. */
export function isSearching(study: PageStudy): boolean {
  return study.pages.some(isSearchingPage);
}

export function usePageStudies(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.pageStudies(workspaceId),
    queryFn: () => fetchPageStudies(workspaceId),
  });
}

export function usePageStudy(workspaceId: string, studyId: string | null) {
  return useQuery({
    queryKey: queryKeys.pageStudy(workspaceId, studyId ?? ""),
    queryFn: () => fetchPageStudy(workspaceId, studyId ?? ""),
    enabled: studyId !== null,
    refetchInterval: (query) =>
      query.state.data !== undefined && isSearching(query.state.data)
        ? SEARCH_POLL_MS
        : false,
  });
}

/**
 * A page's hits. Read again while its study is still searching, since a
 * capture coming back can add one.
 */
export function usePageHits(
  workspaceId: string,
  studyId: string,
  pageId: string,
  isLive: boolean,
) {
  return useQuery({
    queryKey: queryKeys.pageHits(workspaceId, studyId, pageId),
    queryFn: () => fetchPageHits(workspaceId, studyId, pageId),
    refetchInterval: isLive ? SEARCH_POLL_MS : false,
  });
}

export function useStartPageStudy(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePageStudy) => startPageStudy(workspaceId, body),
    onSuccess: async (study) => {
      if (study === "unavailable") return;
      client.setQueryData(queryKeys.pageStudy(workspaceId, study.id), study);
      await client.invalidateQueries({ queryKey: queryKeys.pageStudies(workspaceId) });
    },
  });
}

export function useAddPageHitsToBrand(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { reportId: string; texts: string[] }) =>
      addPageHitsToBrand(workspaceId, body),
    // The questions are the library's now: the library and every page's hits
    // (`inLibrary`) read differently from here on.
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.promptLibrary(workspaceId) });
      await client.invalidateQueries({ queryKey: queryKeys.pageHitsAll(workspaceId) });
    },
  });
}
